import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api, formatRp } from "@/lib/api";
import { Send, Bot, User, Sparkles, ExternalLink } from "lucide-react";

const samples = [
  "Pak saya order Ayim Karkas 350kg, Minyok Gorang 10dus.",
  "Halo, mau pesan Beras Premium 500 kg dan Telur 200 kg.",
  "Order Daging Sapi 50kg untuk SPPG kami besok.",
];

export default function AIChat() {
  const nav = useNavigate();
  const [messages, setMessages] = useState([
    { role: "bot", text: "Halo! Saya TokoInduk AI. Silakan ketik pesanan Anda dengan bahasa natural seperti chat WhatsApp." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`sess-${Math.random().toString(36).slice(2)}`);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (txt) => {
    const message = (txt ?? input).trim();
    if (!message || loading) return;
    setMessages(m => [...m, { role: "user", text: message }]);
    setInput("");
    setLoading(true);
    try {
      const partner_phone = localStorage.getItem("ti_partner_phone") || undefined;
      const r = await api.post("/ai/chat", { session_id: sessionId.current, message, partner_phone });
      const d = r.data;
      setMessages(m => [...m, {
        role: "bot",
        text: d.ai_reply,
        parsed: d.parsed_items,
        order_id: d.order_id,
        total: d.total,
      }]);
    } catch (err) {
      toast.error("AI error. Coba lagi.");
    }
    setLoading(false);
  };

  return (
    <div className="tech-grid min-h-[calc(100vh-4rem)] py-6 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-4">
          <div className="pill-cyan mb-2"><Sparkles className="w-3 h-3"/> AI ORDER PROCESSING · GPT-5.6 TERRA</div>
          <h1 className="font-display text-3xl font-bold text-white">WhatsApp AI Ordering</h1>
          <p className="text-slate-400 text-sm">Ketik seperti WhatsApp. AI mengoreksi typo & mencocokkan katalog otomatis.</p>
        </div>

        <Card className="glass glow-border bg-transparent rounded-2xl overflow-hidden flex flex-col h-[68vh]">
          <div className="flex items-center gap-2 border-b border-slate-800 p-4 bg-slate-900/40">
            <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-emerald-400"/>
            </div>
            <div>
              <div className="text-sm font-semibold text-white">TokoInduk AI · Customer Service</div>
              <div className="text-[10px] text-emerald-400 font-mono-metric flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"/> ONLINE
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl p-3 text-sm ${
                  m.role === "user"
                    ? "bg-emerald-500/15 border border-emerald-500/30 rounded-tr-sm text-slate-100"
                    : "bg-slate-800/70 border border-cyan-500/20 rounded-tl-sm text-slate-100"
                }`}>
                  <div className="flex items-center gap-1.5 text-[10px] font-mono-metric mb-1 opacity-70">
                    {m.role === "user" ? <User className="w-3 h-3"/> : <Bot className="w-3 h-3 text-cyan-400"/>}
                    {m.role === "user" ? "MITRA" : "TOKOINDUK AI"}
                  </div>
                  <div>{m.text}</div>
                  {m.parsed && m.parsed.length > 0 && (
                    <div className="mt-3 bg-slate-900/60 border border-slate-700 rounded-lg p-3">
                      <div className="text-[10px] text-cyan-400 font-mono-metric mb-2">DETAIL PESANAN DIKORESKI</div>
                      <div className="space-y-1 text-xs">
                        {m.parsed.map((it, j) => (
                          <div key={j} className="flex justify-between">
                            <span>{it.product_name}</span>
                            <span className="font-mono-metric text-cyan-300">{it.quantity} {it.unit}</span>
                          </div>
                        ))}
                        <div className="border-t border-slate-700 mt-2 pt-2 flex justify-between font-mono-metric text-emerald-300">
                          <span>TOTAL</span><span>{formatRp(m.total)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {m.order_id && (
                    <button
                      data-testid={`open-confirm-${m.order_id}`}
                      onClick={() => nav(`/confirm/${m.order_id}`)}
                      className="mt-3 w-full text-xs font-mono-metric text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 rounded-lg py-2 flex items-center justify-center gap-2"
                    >
                      Buka Link Konfirmasi <ExternalLink className="w-3 h-3"/>
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-800/70 border border-cyan-500/20 rounded-2xl rounded-tl-sm p-3 text-sm text-cyan-300 flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce"/>
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{animationDelay:"0.1s"}}/>
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{animationDelay:"0.2s"}}/>
                </div>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          <div className="p-3 border-t border-slate-800 bg-slate-900/40">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {samples.map((s, i) => (
                <button
                  key={i}
                  data-testid={`sample-${i}`}
                  onClick={() => send(s)}
                  className="whitespace-nowrap text-xs px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300 hover:border-cyan-500/50 hover:text-cyan-300 transition"
                >{s}</button>
              ))}
            </div>
            <div className="flex gap-2 mt-1">
              <Input
                data-testid="chat-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && send()}
                placeholder="Ketik pesanan Anda…"
                className="flex-1 bg-slate-900/60 border-slate-700 text-white h-11"
              />
              <Button
                data-testid="chat-send-btn"
                onClick={() => send()}
                disabled={loading}
                className="h-11 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold"
              >
                <Send className="w-4 h-4"/>
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
